import { useState, useEffect, useRef } from "react";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { 
  Upload, 
  FileText, 
  Image, 
  Trash2, 
  Download,
  CheckCircle,
  AlertCircle,
  Loader2
} from "lucide-react";

const DocumentUpload = ({ enrollmentId, onUploadComplete }) => {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchDocumentTypes();
    fetchDocuments();
  }, [enrollmentId]);

  const fetchDocumentTypes = async () => {
    try {
      const response = await fetch(`${API}/documents/types`, { credentials: 'include' });
      if (response.ok) {
        setDocumentTypes(await response.json());
      }
    } catch (error) {
      console.error("Error fetching document types:", error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const url = enrollmentId 
        ? `${API}/documents/me?enrollment_id=${enrollmentId}`
        : `${API}/documents/me`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        setDocuments(await response.json());
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!selectedType) {
      toast.error("Please select a document type first");
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload an image or PDF.");
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB.");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const url = new URL(`${API}/documents/upload`);
      url.searchParams.append('document_type', selectedType);
      if (enrollmentId) {
        url.searchParams.append('enrollment_id', enrollmentId);
      }

      const response = await fetch(url.toString(), {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        toast.success("Document uploaded successfully!");
        fetchDocuments();
        setSelectedType("");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        if (onUploadComplete) {
          onUploadComplete();
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to upload document");
      }
    } catch (error) {
      toast.error("An error occurred during upload");
    } finally {
      setUploading(false);
    }
  };

  const deleteDocument = async (documentId) => {
    try {
      const response = await fetch(`${API}/documents/${documentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Document deleted");
        fetchDocuments();
      } else {
        toast.error("Failed to delete document");
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const downloadDocument = async (documentId, filename) => {
    try {
      const response = await fetch(`${API}/documents/${documentId}/download`, {
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        toast.error("Failed to download document");
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const getDocumentIcon = (contentType) => {
    if (contentType?.startsWith('image/')) {
      return <Image className="w-5 h-5 text-[#3B82F6]" />;
    }
    return <FileText className="w-5 h-5 text-[#1E3A8A]" />;
  };

  const getDocumentTypeName = (typeId) => {
    return documentTypes.find(dt => dt.id === typeId)?.name || typeId;
  };

  const uploadedTypes = documents.map(d => d.document_type);
  const requiredTypes = documentTypes.filter(dt => dt.required);
  const completedRequired = requiredTypes.filter(dt => uploadedTypes.includes(dt.id));

  return (
    <div className="space-y-6" data-testid="document-upload">
      {/* Upload Section */}
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Upload className="w-5 h-5 text-brand-navy" />
            Upload Documents
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progress indicator for required docs */}
          <div className="p-4 bg-surface-muted rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600">Required Documents</span>
              <span className="text-sm text-brand-navy font-medium">
                {completedRequired.length} / {requiredTypes.length}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {requiredTypes.map(dt => (
                <div 
                  key={dt.id}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                    uploadedTypes.includes(dt.id) 
                      ? 'bg-[#10B981]/10 text-[#059669]' 
                      : 'bg-[#F59E0B]/10 text-[#D97706]'
                  }`}
                >
                  {uploadedTypes.includes(dt.id) ? (
                    <CheckCircle className="w-3 h-3" />
                  ) : (
                    <AlertCircle className="w-3 h-3" />
                  )}
                  {dt.name}
                </div>
              ))}
            </div>
          </div>

          {/* Upload form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm text-slate-600">Document Type</label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="input-base" data-testid="select-document-type">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {documentTypes.map(dt => (
                    <SelectItem key={dt.id} value={dt.id}>
                      <div className="flex items-center gap-2">
                        {dt.name}
                        {dt.required && <span className="text-brand-navy text-xs">*</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedType && (
                <p className="text-xs text-slate-500">
                  {documentTypes.find(dt => dt.id === selectedType)?.description}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm text-slate-600">File</label>
              <div className="relative">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="document-upload"
                  disabled={!selectedType || uploading}
                />
                <label
                  htmlFor="document-upload"
                  className={`
                    flex items-center justify-center gap-2 w-full py-3 px-4 
                    border-2 border-dashed border-slate-200 rounded-lg cursor-pointer
                    hover:border-brand-gold/50 transition-colors
                    ${!selectedType || uploading ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-5 h-5 text-brand-navy animate-spin" />
                      <span className="text-slate-500">Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-brand-navy" />
                      <span className="text-slate-500">Choose file</span>
                    </>
                  )}
                </label>
              </div>
              <p className="text-xs text-slate-500">JPG, PNG, GIF, WebP, or PDF (max 10MB)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Uploaded Documents */}
      {documents.length > 0 && (
        <Card className="card-surface">
          <CardHeader>
            <CardTitle className="text-slate-900">Uploaded Documents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {documents.map(doc => (
                <div 
                  key={doc.document_id}
                  className="flex items-center justify-between p-4 bg-surface-muted rounded-lg"
                  data-testid={`document-${doc.document_id}`}
                >
                  <div className="flex items-center gap-4">
                    {getDocumentIcon(doc.content_type)}
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{doc.original_filename}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span className="px-2 py-0.5 bg-brand-gold/10 text-brand-navy rounded">
                          {getDocumentTypeName(doc.document_type)}
                        </span>
                        <span>{(doc.size / 1024).toFixed(1)} KB</span>
                        <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => downloadDocument(doc.document_id, doc.original_filename)}
                      className="text-slate-400 hover:text-slate-900"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteDocument(doc.document_id)}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DocumentUpload;
